import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Divider,
  Avatar,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
// import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PaymentsIcon from "@mui/icons-material/Payments";

import PageLayout from "../../components/layout/PageLayout";
// import Can from '../../components/permissions/Can';
import { useGetPaymentByIdQuery } from "../../store/api";
import { toast } from "react-toastify";
import { buildPaymentRelationParams } from "./paymentApiHelpers";

import {
  hasLeadData,
  getLeadNameDisplay,
  getLeadEmailDisplay,
  getLeadPhoneDisplay,
  getPaymentTypeLabel,
  getGatewayIdDisplay,
  getTransactionIdDisplay,
  getAmountDisplay,
  getTimeDisplay,
  // getTypeDisplay,
  getStatusDisplay,
  getClientIpDisplay,
  getNoteDisplay,
  getCcTypeDisplay,
  getInvoiceStatusDisplay,
  getInvoiceUrlDisplay,
  getInvoiceTypeOfPaymentDisplay,
  getInvoiceSaleTypeDisplay,
  // getInvoiceShouldSendContractDisplay,
  getInvoiceCreatedAtDisplay,
  // getInvoiceDescriptionDisplay,
} from "./paymentRowMappers";

const statusChipColor = (label) => {
  const lower = String(label || "").toLowerCase();

  if (
    ["paid", "completed", "success", "approved"].some((x) => lower.includes(x))
  )
    return "success";
  if (
    ["pending", "processing", "partial", "unpaid"].some((x) =>
      lower.includes(x),
    )
  )
    return "warning";
  if (
    ["failed", "cancelled", "declined", "error"].some((x) => lower.includes(x))
  )
    return "error";
  if (["refunded"].some((x) => lower.includes(x))) return "info";
  return "default";
};

const InfoItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{ fontWeight: 500, wordBreak: "break-word" }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const ViewPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetPaymentByIdQuery(
    { id, params: buildPaymentRelationParams() },
    { skip: !id },
  );

  const payment = data?.data ?? data ?? null;
  console.log("payment==>", payment);

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || "Failed to load payment");
      navigate("/payments");
    }
  }, [isError, error, navigate]);

  if (loading || !payment) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const statusLabel = getStatusDisplay(payment);
  const transactionId = getTransactionIdDisplay(payment);
  const amount = getAmountDisplay(payment);
  const invoiceUrl = getInvoiceUrlDisplay(payment);

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate("/payments")}
          >
            Back
          </Button>

          <Chip
            label={statusLabel}
            color={statusChipColor(statusLabel)}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* HEADER CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 5,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))",
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main" }}>
              <PaymentsIcon />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Payment Details
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Transaction: <b>{transactionId}</b>
              </Typography>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              ${amount}
            </Typography>
          </Stack>
        </Paper>

        {/* CUSTOMER */}
        {hasLeadData(payment) && (
          <Card sx={{ mb: 3, borderRadius: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Customer
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <InfoItem label="Name" value={getLeadNameDisplay(payment)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoItem
                    label="Email"
                    value={getLeadEmailDisplay(payment)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoItem
                    label="Phone"
                    value={getLeadPhoneDisplay(payment)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* PAYMENT */}
        <Card sx={{ mb: 3, borderRadius: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InfoItem label="Transaction ID" value={transactionId} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoItem label="Time" value={getTimeDisplay(payment)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoItem
                  label="Gateway"
                  value={getGatewayIdDisplay(payment)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <InfoItem
                  label="Payment Type"
                  value={getPaymentTypeLabel(payment)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoItem label="Card Type" value={getCcTypeDisplay(payment)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoItem
                  label="Client IP"
                  value={getClientIpDisplay(payment)}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <InfoItem label="Note" value={getNoteDisplay(payment)} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* INVOICE */}
        {payment.invoice && (
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Invoice
              </Typography>

              <Grid container spacing={2}>
                {/* <Grid item xs={12} sm={4}>
                  <InfoItem
                    label="Invoice ID"
                    value={payment?.invoice?.id ?? "—"}
                  />
                </Grid> */}

                <Grid item xs={12} sm={4}>
                  <InfoItem
                    label="Account Name"
                    value={payment?.invoice?.account?.name}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoItem
                    label="Status"
                    value={getInvoiceStatusDisplay(payment)}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <InfoItem
                    label="Created At"
                    value={getInvoiceCreatedAtDisplay(payment)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InfoItem
                    label="Sale Type"
                    value={getInvoiceSaleTypeDisplay(payment)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InfoItem
                    label="Payment Type"
                    value={getInvoiceTypeOfPaymentDisplay(payment)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Invoice URL</Typography>

                    {invoiceUrl ? (
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={async () => {
                          await navigator.clipboard.writeText(invoiceUrl);
                          toast.success("Copied");
                        }}
                      >
                        Copy
                      </Button>
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Container>
    </PageLayout>
  );
};

export default ViewPayment;
